import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Select,
  Button,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react'
import { useCreateUser } from 'queries/user.query'

// Validation helper functions
const isValidUsername = username => /^[a-zA-Z0-9 ]{3,}$/.test(username)
const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
const isValidPassword = password => password.length >= 8

export const CreateUserModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: 'admin',
    password: '',
    confirmPassword: '',
  })
 const toast = useToast();
  const [formErrors, setFormErrors] = useState({})
  const [passwordMatchError, setPasswordMatchError] = useState('')

  const { mutate: createNewUser, isPending } =
    useCreateUser()

  const inputColor = useColorModeValue('gray.800', 'white')
  const backgroundColor = useColorModeValue('white', 'gray.700')

  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateForm = () => {
    const errors = {}

    if (!isValidUsername(formData.username)) {
      errors.username = 'Username must be at least 3 characters long.'
    }

    if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address.'
    }

    if (!isValidPassword(formData.password)) {
      errors.password = 'Password must be at least 8 characters long.'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleCreateUser = e => {
    e.preventDefault()
    setPasswordMatchError('')

    if (!validateForm()) {
      return
    }

    if (formData.password !== formData.confirmPassword) {
      setPasswordMatchError('Passwords do not match.')
      return
    }

    createNewUser(formData, {
      onSuccess: () => {
        onClose()
        setFormData({
          username: '',
          email: '',
          role: 'admin',
          password: '',
          confirmPassword: '',
        })
        toast({
          title: 'User Created.',
          description: 'The new user has been successfully added.',
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        })
      },
     onError: () => {
        toast({
          title: 'Failed to Create User.',
          description:
            // err.response?.data?.message ||
            'The email was already taken.',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        })
      },
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New User</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl isRequired>
            <FormLabel>Name</FormLabel>
            <Input
              type='text'
              name='username'
              value={formData.username}
              onChange={handleInputChange}
              color={inputColor}
              backgroundColor={backgroundColor}
            />
            {formErrors.username && (
              <Text color='red.500' fontSize='sm' mt={1}>
                {formErrors.username}
              </Text>
            )}
          </FormControl>

          <FormControl isRequired mt={4}>
            <FormLabel>Email</FormLabel>
            <Input
              type='email'
              name='email'
              value={formData.email}
              onChange={handleInputChange}
              color={inputColor}
              backgroundColor={backgroundColor}
            />
            {formErrors.email && (
              <Text color='red.500' fontSize='sm' mt={1}>
                {formErrors.email}
              </Text>
            )}
          </FormControl>

          <FormControl isRequired mt={4}>
            <FormLabel>Role & Permissions</FormLabel>
            <Select
              name='role'
              value={formData.role}
              onChange={handleInputChange}
              color={inputColor}
              backgroundColor={backgroundColor}
            >
              <option value='admin'>Admin (Full System Access)</option>
              <option value='gate_keeper'>Gate Keeper (Ticket Scanner Only)</option>
            </Select>
            <Text color='gray.500' fontSize='xs' mt={1}>
              {formData.role === 'gate_keeper'
                ? 'Gate Keepers only have access to the mobile ticket scanner and cannot view financial or system data.'
                : 'Admins have full access to events, seating, vouchers, and financial reports.'}
            </Text>
          </FormControl>

          <FormControl isRequired mt={4}>
            <FormLabel>Password</FormLabel>
            <Input
              type='password'
              name='password'
              value={formData.password}
              onChange={handleInputChange}
              color={inputColor}
              backgroundColor={backgroundColor}
            />
            {formErrors.password && (
              <Text color='red.500' fontSize='sm' mt={1}>
                {formErrors.password}
              </Text>
            )}
          </FormControl>

          <FormControl isRequired mt={4}>
            <FormLabel>Confirm Password</FormLabel>
            <Input
              type='password'
              name='confirmPassword'
              value={formData.confirmPassword}
              onChange={handleInputChange}
              color={inputColor}
              backgroundColor={backgroundColor}
            />
            {passwordMatchError && (
              <Text color='red.500' fontSize='sm' mt={1}>
                {passwordMatchError}
              </Text>
            )}
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme='blue'
            type='submit'
            isLoading={isPending}
            onClick={handleCreateUser}
            isDisabled={isPending}
          >
            Create
          </Button>
          <Button ml={3} colorScheme='gray' onClick={onClose}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
